import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "../lib/api";

export default function PaymentResult({ outcome }) {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [result, setResult] = useState(null);
    const [attempts, setAttempts] = useState(0);

    useEffect(() => {
        if (outcome !== "success" || !sessionId) return;
        const poll = () => {
            api.get(`/payments/status/${sessionId}`)
                .then((r) => {
                    setResult(r.data);
                    if (r.data.payment_status !== "paid" && attempts < 10) {
                        setAttempts((a) => a + 1);
                    }
                })
                .catch(() => setAttempts((a) => a + 1));
        };
        poll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attempts]);

    if (outcome === "cancel")
        return (
            <main className="min-h-[60vh] bg-slate-50 flex items-center justify-center px-4" data-testid="payment-cancel">
                <div className="bg-white rounded-2xl border border-slate-200 p-10 max-w-md text-center">
                    <XCircle className="h-14 w-14 text-amber-500 mx-auto mb-4" />
                    <h1 className="font-heading text-2xl font-extrabold text-slate-900 mb-2">Payment cancelled</h1>
                    <p className="text-slate-500 text-sm mb-6">No money was taken. Your items are still waiting if you'd like to try again.</p>
                    <Link to="/products" data-testid="cancel-back-link"
                        className="inline-block rounded-full bg-[#0033A0] px-8 py-3 font-heading font-bold text-white hover:bg-[#002277]">
                        Continue Shopping
                    </Link>
                </div>
            </main>
        );

    if (!result || result.payment_status !== "paid")
        return (
            <main className="min-h-[60vh] bg-slate-50 flex items-center justify-center px-4" data-testid="payment-processing">
                <div className="bg-white rounded-2xl border border-slate-200 p-10 max-w-md text-center">
                    <Loader2 className="h-14 w-14 text-[#0033A0] mx-auto mb-4 animate-spin" />
                    <h1 className="font-heading text-2xl font-extrabold text-slate-900 mb-2">Confirming your payment…</h1>
                    <p className="text-slate-500 text-sm">This usually takes a few seconds.</p>
                </div>
            </main>
        );

    return (
        <main className="min-h-[60vh] bg-slate-50 flex items-center justify-center px-4" data-testid="payment-success">
            <div className="bg-white rounded-2xl border border-slate-200 p-10 max-w-md text-center">
                <CheckCircle2 className="h-14 w-14 text-green-600 mx-auto mb-4" />
                <h1 className="font-heading text-2xl font-extrabold text-slate-900 mb-2" data-testid="success-heading">
                    Thank you — payment confirmed!
                </h1>
                <p className="text-slate-500 text-sm mb-2">Your order number is</p>
                <p className="font-heading text-3xl font-extrabold text-[#0033A0] mb-6" data-testid="success-order-number">
                    {result.order_number}
                </p>
                <p className="text-slate-500 text-sm mb-6">
                    We've emailed your confirmation. Keep your order number safe — you can use it with your tracking link to follow your order.
                </p>
                <div className="flex flex-col gap-3">
                    <Link to={`/track/${result.order_number}?token=${result.tracking_token}`} data-testid="success-track-link"
                        className="rounded-full bg-[#40E0D0] px-8 py-3 font-heading font-bold text-[#0033A0] hover:bg-[#2fd4c4]">
                        Track Your Order
                    </Link>
                    <Link to="/products" data-testid="success-shop-link" className="text-[#0033A0] font-bold hover:underline text-sm">
                        Continue Shopping
                    </Link>
                </div>
            </div>
        </main>
    );
}
